import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * Drop-in replacement for Xano's partner-detail call, so the original screen
 * keeps its layout and only its data source changes.
 *
 * A caveat worth reading before trusting this screen. Xano's `channels` table
 * carried about twenty-five fields per partner; the migration brought across
 * nine — name, type, level, parent, email, phone, city, state, status. The
 * rest (GST number, masked PAN and Aadhaar, address lines, pincode, property
 * type, branch type, RM name, stage tags, Zoho contract status, whether
 * credentials were shared) were never copied, and the Xano token has since
 * been rotated, so they cannot be re-pulled from here.
 *
 * Those fields come back as "NA" rather than as blanks or invented values.
 * The screen was built for that — it runs every field through a
 * `getValueOrNA` helper — so it renders honestly and shows exactly how much
 * of a partner record is missing.
 */

type Payload<T> = AxiosRequestConfig<T>;

/** What the migration did not carry. Shown as NA, never guessed. */
const NOT_MIGRATED = "NA";

type OrganizationDetail = {
  id: string;
  name: string;
  type: string;
  partnerLevel: number | null;
  status: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  createdAt?: string;
  parent: { id: string; name: string } | null;
  /** The relationship manager, when one has been set. */
  assignedUser?: { id: string; fullName: string; email?: string } | null;
  children: { id: string; name: string; type: string }[];
  childrenTotal?: number;
  users: {
    fullName: string;
    email: string;
    role: string | null;
    status: string;
  }[];
};

export type GetPartnerDetailsRequestType = Partial<{
  /** Both spellings are in use across this screen's two callers. */
  channels_id: string | number;
  channel_id: string | number;
  headers: { Authorization: string };
}>;

/* eslint-disable @typescript-eslint/no-explicit-any */
export async function getPartnerDetails(
  payload: Payload<GetPartnerDetailsRequestType> & {
    params?: { channel_id?: string | number };
  },
  // `any` on purpose: this screen reads dozens of nested Xano fields, and a
  // narrower type here would only move the errors into the screen.
): Promise<{ data: any }> {
  const id =
    payload.data?.channels_id ??
    payload.data?.channel_id ??
    payload.params?.channel_id;
  if (id === undefined) throw new Error("A partner id is required.");

  const { data } = await moneefloApi.get<OrganizationDetail>(
    `/organizations/${id}`,
  );

  return {
    data: {
      channel_info: {
        id: data.id,
        name: data.name,
        phone: data.phone ?? NOT_MIGRATED,
        email: data.email ?? NOT_MIGRATED,
        status: data.status,
        type: data.type,
        city: data.city ?? NOT_MIGRATED,
        state: data.state ?? NOT_MIGRATED,
        approval_status: data.status === "active" ? "approved" : data.status,
        branch_type: data.partnerLevel ? `L${data.partnerLevel}` : NOT_MIGRATED,
        // Epoch renders as 01-Jan-1970 on this screen, so send the real date.
        created_at: data.createdAt ?? null,

        // Never migrated from Xano's channels table.
        pan_type: NOT_MIGRATED,
        masked_pan_no: NOT_MIGRATED,
        masked_aadhar_no: NOT_MIGRATED,
        address_line_1: NOT_MIGRATED,
        address_line_2: NOT_MIGRATED,
        pincode: NOT_MIGRATED,
        property_type: NOT_MIGRATED,
        gst_number: NOT_MIGRATED,
        // The screen reads the manager off `_rm.fname`, as Xano shaped it.
        _rm: data.assignedUser
          ? { id: data.assignedUser.id, fname: data.assignedUser.fullName }
          : null,
        _rm_name: data.assignedUser?.fullName ?? NOT_MIGRATED,
        digital_interest: NOT_MIGRATED,
        stage_tags: [],
        credential_shared: NOT_MIGRATED,
        zoho_contract_status: NOT_MIGRATED,
        extra_detail: null,
        note: null,
        channels_id: data.id,
        interactions_id: null,
        isPrimary: false,
        relation_with_partner: NOT_MIGRATED,
        linked_partner_id: data.parent ? data.parent.id : null,
        partner_link_time: null,
        checklist_completed: false,

        // Relations Xano nested inside channel_info. Empty is how the screen
        // reads "none on file"; none of these are migrated yet.
        _bank_account_of_channels: [],
        _channel_commission: [],
        _onboarding_checklist: [],
        _created_by: null,
        _partner_linked_by: null,
        products: [],
        docs: [],

        // Documents live in object storage, which has no public address yet.
        // An empty url is how this screen says "no document on file".
        pan: { url: "" },
        aadhar_front: { url: "" },
        aadhar_back: NOT_MIGRATED,
        address_proof: { url: "" },
        selfie: { url: "" },
        photos: [],
      },
      _branch: data.parent
        ? { id: data.parent.id, name: data.parent.name }
        : null,
      team_member: data.users.map((user) => ({
        id: user.email,
        name: user.fullName,
        email: user.email,
        role: user.role ?? NOT_MIGRATED,
        status: user.status,
      })),
      // Onboarding checklists and the reshare flow were Xano workflow state and
      // have no equivalent yet; an empty checklist reads as "nothing pending".
      checklist: [],
      completed: false,
      reshare_button: false,
    },
  };
}

/**
 * The corporate lead screen's fetch. A corporate client is an organization of
 * type `corporate` here, so it reads from the same endpoint as a partner and
 * is reshaped into the flat company object that screen expects.
 */
export type GetSingleCompanyRequestType = Partial<{
  /** The corporate screen sends entity_id; other callers use company_id. */
  entity_id: string | number;
  company_id: string | number;
  headers: { Authorization: string };
}>;

export async function getSingleCompanyLeads(
  payload: Payload<GetSingleCompanyRequestType> & {
    params?: { entity_id?: string | number; company_id?: string | number };
  },
): Promise<{ data: any }> {
  const id =
    payload.params?.entity_id ??
    payload.params?.company_id ??
    payload.data?.entity_id ??
    payload.data?.company_id;
  if (id === undefined) throw new Error("A company id is required.");

  const { data } = await moneefloApi.get<OrganizationDetail>(
    `/organizations/${id}`,
  );

  return {
    data: {
      id: data.id,
      entity_name: data.name,
      email: data.email ?? NOT_MIGRATED,
      phone: data.phone ?? NOT_MIGRATED,
      city: data.city ?? NOT_MIGRATED,
      state: data.state ?? NOT_MIGRATED,
      status: data.status,
      // getRelativeDate takes epoch milliseconds, not an ISO string.
      created_at: data.createdAt ? new Date(data.createdAt).getTime() : null,
      unique_id: data.id,
      address_id: [],
      extra_detail: null,

      // Xano's corporate record carried these; the migration did not.
      gst_number: NOT_MIGRATED,
      pan_no: NOT_MIGRATED,
      digital_interest: NOT_MIGRATED,
      stage_tags: [],
      _contacts: [],
      _company_documents: [],
      team_member: data.users.map(function (user) {
        return {
          id: user.email,
          contactName: user.fullName,
          contactEmail: user.email,
          contactNumber: "",
          role: user.role ?? NOT_MIGRATED,
          status: user.status,
        };
      }),
    },
  };
}

/* ------------------------------------------------------------------ *
 * Corporate account management
 *
 * The corporate lead page could be read but not acted on: reassigning
 * and logging an interaction both still pointed at Xano, and the shared
 * client rejects those before they leave the browser — which is why the
 * network tab stayed empty while a toast said it had failed.
 *
 * These keep the old signatures so the page does not have to change.
 * ------------------------------------------------------------------ */

export type EditCompanyRequest = Partial<{
  coorporate_company_id: string | number;
  staff_id: string | number;
  reminder_time: string;
  reminder_note: string;
  removeReminder: boolean;
  lead_status: string;
  note: string;
  branch: string;
}>;

/**
 * Reassign, and the lead-status dropdown beside it.
 *
 * Reminders and notes are not carried: the API has nowhere to put them
 * yet, and silently dropping them would be worse than saying so. The
 * call still succeeds for the parts that do exist, so reassigning works
 * even while a reminder set at the same time does not.
 */
export async function EditCompany(
  payload: Payload<EditCompanyRequest> & { data?: EditCompanyRequest },
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  const id = body.coorporate_company_id;
  if (id === undefined) throw new Error("A company id is required.");

  const patch: Record<string, unknown> = {};
  if (body.staff_id !== undefined && body.staff_id !== "") {
    patch.assignedUserId = String(body.staff_id);
  }
  if (body.lead_status) patch.status = body.lead_status;

  if (Object.keys(patch).length === 0) {
    // Nothing the API can act on — most likely a reminder-only save.
    return { status: 200, data: { skipped: true } };
  }

  const { data } = await moneefloApi.patch(
    `/organizations/${id}/assignment`,
    patch,
  );
  return { status: 200, data };
}

export type CorpAddInteractionsReqType = Partial<{
  note: string;
  meeting_time: string;
  meetingStaff_id: number[];
  channel_of_interaction_id: string | number;
  contact: string;
  company_id: string | number;
  selfie_image: string;
  visiting_card: string;
  quotation_pdf: string;
  geo_coordinates: { type: string; data: { lng?: string; lat?: string } };
}>;

/** Xano's channel ids, which the dropdown still sends. */
const INTERACTION_CHANNEL: Record<string, string> = {
  "1": "call",
  "2": "email",
  "3": "visit",
  "4": "whatsapp",
  "5": "meeting",
};

/**
 * Records a call or a visit against a corporate client.
 *
 * The selfie and visiting card are not carried yet — they need the
 * document store wiring up — so the note, the channel, the meeting time
 * and the coordinates are kept and the attachments are not. Who logged
 * it comes from the session on the server, not from here.
 */
export async function corpAddInteractions(
  payload: Payload<CorpAddInteractionsReqType> & {
    data?: CorpAddInteractionsReqType;
  },
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  if (body.company_id === undefined) throw new Error("A company id is required.");
  if (!body.note?.trim()) throw new Error("A note is required.");

  const lat = Number(body.geo_coordinates?.data?.lat);
  const lng = Number(body.geo_coordinates?.data?.lng);

  const { data } = await moneefloApi.post(
    `/organizations/${body.company_id}/interactions`,
    {
      note: body.note,
      channel:
        INTERACTION_CHANNEL[String(body.channel_of_interaction_id ?? "")] ??
        "other",
      direction: "outbound",
      ...(body.meeting_time
        ? { meetingAt: new Date(body.meeting_time).toISOString() }
        : {}),
      ...(Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : {}),
    },
  );
  return { status: 200, data };
}

/** What has been said to this client, for the Interaction tab. */
export async function getCorporateInteractions(companyId: string | number) {
  const { data } = await moneefloApi.get<{
    items: Array<{
      id: string;
      note: string | null;
      channel: string;
      occurredAt: string;
      meetingAt: string | null;
      by: string | null;
    }>;
  }>(`/organizations/${companyId}/interactions`);
  return data.items;
}
